import {
    BankOutlined,
    BuildOutlined,
    CalendarOutlined,
    CarOutlined,
    DownloadOutlined,
    EyeOutlined,
    FileProtectOutlined,
    FileTextOutlined,
    FilterOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { Button, Card, Col, DatePicker, message, Row, Select, Space, Tabs, Typography, Tag, Tooltip, Divider } from 'antd';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import { TrendingUp } from 'lucide-react';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import SDIModal from '../../../components/ui/SDIModal';
import EmissionsNettesCedeesParExerciceEtCategorie from './components/EmissionsNettesCedeesParExerciceEtCategorie';
import EtatGlobalProductionMensuelDTRFNational from './components/EtatGlobalProductionMensuelDTRFNational';
import dayjs from 'dayjs';
import { exportEmissionsNettesToExcel, exportEmissionsNettesToPDF, exportGUMProductionToExcel, exportGUMProductionToPDF } from './utils/ExportUtils';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// ═══════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════
const theme = {
    colors: {
        primary: '#242166',
        primaryLight: '#3730a3',
        secondary: '#1e40af',
        accent: '#f59e0b',
        accentLight: '#fbbf24',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        info: '#3b82f6',
        background: '#f8fafc',
        surface: '#ffffff',
        surfaceHover: '#f1f5f9',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        textMuted: '#9ca3af',
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
    },
    shadows: {
        sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
        xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
    },
    radius: {
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '20px',
    },
};

// Styles
const styles = {
    pageBackground: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.colors.background} 0%, #e2e8f0 100%)`,
    },
    header: {
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
        padding: '24px 32px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden',
    },
    headerPattern: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '40%',
        background: 'radial-gradient(circle at 70% 50%, rgba(245, 158, 11, 0.15) 0%, transparent 50%)',
        pointerEvents: 'none',
    },
    filterCard: {
        borderRadius: theme.radius.lg,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colors.border}`,
        marginBottom: '24px',
    },
    filterSection: {
        padding: '24px',
    },
    filterLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
    },
    filterIcon: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
    },
    tabsCard: {
        borderRadius: theme.radius.lg,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colors.border}`,
        marginBottom: '24px',
    },
    reportCard: {
        height: '100%',
        borderRadius: theme.radius.lg,
        overflow: 'hidden',
        border: 'none',
        boxShadow: theme.shadows.md,
        transition: 'all 0.3s ease',
    },
    reportCardHeader: {
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
    },
    reportCardBody: {
        padding: '24px',
    },
    btn: {
        height: '44px',
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '14px',
    },
    btnPrimary: {
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
        border: 'none',
        boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)',
    },
    btnOutline: {
        border: `2px solid ${theme.colors.border}`,
        background: 'transparent',
    },
    periodBadge: {
        background: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        padding: '16px 24px',
        display: 'inline-block',
    },
    periodLabel: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '12px',
        marginBottom: '4px',
        display: 'block',
    },
    periodValue: {
        color: '#ffffff',
        fontSize: '15px',
        fontWeight: 600,
    },
};

// Liste des mois pour le filtre
const MONTHS = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
];

// Générer les années (de 2020 à l'année courante + 1)
const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 1; year >= 2020; year--) {
        years.push({ value: year.toString(), label: year.toString() });
    }
    return years;
};

const EtatsProductionGlobal = () => {
    // États existants
    const [branche, setBranche] = useState('TPVM');
    const [categories, setCategories] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [agences, setAgences] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [selectedAgence, setSelectedAgence] = useState(null);

    // Nouveaux états pour la gestion de période
    const [selectedExercice, setSelectedExercice] = useState(null); // Année obligatoire
    const [periodType, setPeriodType] = useState('year'); // 'year', 'month', 'range'
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedRange, setSelectedRange] = useState(null);

    // États de chargement individuels
    const [loadingStates, setLoadingStates] = useState({});

    // États pour les modales
    const [showEmissionNetteCedee, setShowEmissionNetteCedee] = useState(false);
    const [emissions, setEmissions] = useState([]);
    const [showEtatGlobalProductionNationalGUM, setShowEtatGlobalProductionNationalGUM] = useState(false);
    const [joursEGPNGUM, setJoursEGPNGUM] = useState([]);
    const [showEtatGlobalProductionGUM, setShowEtatGlobalProductionGUM] = useState(false);
    const [joursEGPGUM, setJoursEGPGUM] = useState([]);

    // Configuration des rapports par branche
    const typeFiles = {
        TPVM: [
            {
                key: 'emissions_nettes',
                title: 'Émissions nettes cédées',
                description: 'État des émissions nettes cédées de toutes les compagnies sur un exercice donné et par catégorie',
                icon: <TrendingUp style={{ width: '24px', height: '24px' }} />,
                gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#667eea',
            }
        ],
        GUM: [
            {
                key: 'etat_global_national',
                title: 'État global de production',
                description: 'État global de production GUM pour tous les bureaux au niveau national',
                icon: <FileTextOutlined style={{ fontSize: '24px' }} />,
                gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#10b981',
            },
            {
                key: 'etat_production_bureau',
                title: 'État de production par bureau',
                description: 'État de production GUM pour un bureau DTRF spécifique',
                icon: <BankOutlined style={{ fontSize: '24px' }} />,
                gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#f59e0b',
            }
        ],
        VT: [
            {
                key: 'etat_production_vt',
                title: 'État de production VT',
                description: 'État global de production pour la branche Voyage & Tourisme',
                icon: <FileProtectOutlined style={{ fontSize: '24px' }} />,
                gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: '#8b5cf6',
            }
        ]
    };

    const emissionsRef = useRef(null);
    const etatGlobalNationalRef = useRef(null);
    const etatGlobalBureauRef = useRef(null);

    // Chargement initial des données
    useEffect(() => {
        axiosInstance.get(`/api/categorie`)
            .then((res) => setCategories(res.data.filter(e => !e.codeCategorie.startsWith("V"))))
            .catch(console.log);

        axiosInstance.get(`/api/organisme/assurance`)
            .then((res) => setCompagnies(res.data))
            .catch(console.log);

        axiosInstance.get(`/api/agence/dtrf`)
            .then((res) => setAgences(res.data))
            .catch(console.log);
    }, []);

    // Helper pour gérer les états de chargement
    const setLoading = (key, value) => {
        setLoadingStates(prev => ({ ...prev, [key]: value }));
    };

    // Calcul de la période effective
    const getEffectivePeriod = useMemo(() => {
        if (!selectedExercice) return null;

        let startDate, endDate, displayText;

        switch (periodType) {
            case 'month':
                if (selectedMonth) {
                    startDate = `${selectedExercice}-${selectedMonth}-01`;
                    const lastDay = new Date(parseInt(selectedExercice), parseInt(selectedMonth), 0).getDate();
                    endDate = `${selectedExercice}-${selectedMonth}-${lastDay}`;
                    const monthName = MONTHS.find(m => m.value === selectedMonth)?.label;
                    displayText = `${monthName} ${selectedExercice}`;
                } else {
                    return null;
                }
                break;
            case 'range':
                if (selectedRange && selectedRange[0] && selectedRange[1]) {
                    startDate = selectedRange[0].format('YYYY-MM-DD');
                    endDate = selectedRange[1].format('YYYY-MM-DD');
                    displayText = `${selectedRange[0].format('DD/MM/YYYY')} - ${selectedRange[1].format('DD/MM/YYYY')}`;
                } else {
                    // Fallback sur l'année complète
                    startDate = `${selectedExercice}-01-01`;
                    endDate = `${selectedExercice}-12-31`;
                    displayText = `Année ${selectedExercice}`;
                }
                break;
            case 'year':
            default:
                startDate = `${selectedExercice}-01-01`;
                endDate = `${selectedExercice}-12-31`;
                displayText = `Année ${selectedExercice}`;
                break;
        }

        return { startDate, endDate, displayText, exercice: selectedExercice };
    }, [selectedExercice, periodType, selectedMonth, selectedRange]);

    // Fonctions de génération des rapports
    const setupEmissionNetteCedee = async (categorie, exercice) => {
        const { data } = await axiosInstance.get(`/api/production/emissions-nettes-cedees/${categorie}/${exercice}-01`);
        setEmissions(data);
        setShowEmissionNetteCedee(true);
        return data;
    };

    const setupEtatGlobalProductionNationalGUM = async (exercice) => {
        console.log("Fetching national GUM data...", selectedMonth);
        const { data } = await axiosInstance.get(`/api/production/etat-global-production-national-gum/${exercice}-${selectedMonth}`);
        setJoursEGPNGUM(data);
        setShowEtatGlobalProductionNationalGUM(true);
        return data;
    };

    const setupEtatGlobalProductionGUMByAgence = async (exercice, codeAgence) => {
        const { data } = await axiosInstance.get(`/api/production/etat-global-production-gum/${exercice}-${selectedMonth}/${codeAgence}`);
        setJoursEGPGUM(data);
        setShowEtatGlobalProductionGUM(true);
        return data;
    };

    // Handler de prévisualisation
    const handlePreview = async (file) => {
        const key = file.key;
        console.log("Previewing report:", selectedMonth);
        // Validations
        if (!selectedExercice) {
            return message.error('Veuillez sélectionner un exercice (année)');
        }

        if (branche === 'TPVM' && !selectedCategory) {
            return message.error('Veuillez sélectionner une catégorie');
        }

        if (file.key === 'etat_production_bureau' && !selectedAgence) {
            return message.error('Veuillez sélectionner un bureau DTRF');
        }

        // Période effective
        const period = getEffectivePeriod;
        if (!period) {
            return message.error('Veuillez configurer la période');
        }

        try {
            setLoading(key, true);

            switch (file.key) {
                case 'emissions_nettes':
                    await setupEmissionNetteCedee(
                        selectedCategory?.codeCategorie || selectedCategory,
                        period.exercice
                    );
                    break;
                case 'etat_global_national':
                    await setupEtatGlobalProductionNationalGUM(period.exercice);
                    break;
                case 'etat_production_bureau':
                    await setupEtatGlobalProductionGUMByAgence(
                        period.exercice,
                        selectedAgence?.codeAgence || selectedAgence
                    );
                    break;
                default:
                    console.log('Preview for', file.title, branche);
            }
        } catch (err) {
            console.error(err);
            message.error('Une erreur est survenue lors de la génération du rapport');
        } finally {
            setLoading(key, false);
        }
    };

    // Handler de téléchargement
    const handleDownload = async (file) => {
        const key = `dl_${file.key}`;

        // Validations
        if (!selectedExercice) {
            return message.error('Veuillez sélectionner un exercice (année)');
        }

        if (branche === 'TPVM' && !selectedCategory) {
            return message.error('Veuillez sélectionner une catégorie');
        }

        if (file.key === 'etat_production_bureau' && !selectedAgence) {
            return message.error('Veuillez sélectionner un bureau DTRF');
        }

        const period = getEffectivePeriod;
        if (!period) {
            return message.error('Veuillez configurer la période');
        }

        try {
            setLoading(key, true);

            switch (file.key) {
                case 'emissions_nettes':
                    // Load data if not already loaded
                    let emissionsData = emissions;
                    if (emissions.length === 0) {
                        const { data } = await axiosInstance.get(
                            `/api/production/emissions-nettes-cedees/${selectedCategory?.codeCategorie || selectedCategory}/${period.exercice}-01`
                        );
                        emissionsData = data;
                        setEmissions(data);
                    }

                    // Export to Excel and PDF
                    const excelSuccess = exportEmissionsNettesToExcel(
                        emissionsData,
                        selectedCategory?.codeCategorie === "M" ? "MOTO" : selectedCategory?.codeCategorie,
                        period.exercice
                    );
                    const pdfSuccess = exportEmissionsNettesToPDF(
                        emissionsData,
                        selectedCategory?.codeCategorie === "M" ? "MOTO" : selectedCategory?.codeCategorie,
                        period.exercice
                    );

                    if (excelSuccess && pdfSuccess) {
                        message.success('Fichiers Excel et PDF téléchargés avec succès');
                    } else {
                        message.warning('Téléchargement partiel - vérifiez les fichiers');
                    }
                    break;

                case 'etat_global_national':
                    // Load data if not already loaded
                    let joursNational = joursEGPNGUM;
                    if (joursEGPNGUM.length === 0) {
                        console.log("Fetching national GUM data...", selectedMonth);
                        const { data } = await axiosInstance.get(
                            `/api/production/etat-global-production-national-gum/${period.exercice}-01`
                        );
                        joursNational = data;
                        setJoursEGPNGUM(data);
                    }

                    // Export to Excel and PDF
                    const excelNatSuccess = exportGUMProductionToExcel(
                        joursNational,
                        `${period.exercice}-${selectedMonth || '01'}`
                    );
                    const pdfNatSuccess = exportGUMProductionToPDF(
                        joursNational,
                        `${period.exercice}-${selectedMonth || '01'}`
                    );

                    if (excelNatSuccess && pdfNatSuccess) {
                        message.success('Fichiers Excel et PDF téléchargés avec succès');
                    } else {
                        message.warning('Téléchargement partiel - vérifiez les fichiers');
                    }
                    break;

                case 'etat_production_bureau':
                    // Load data if not already loaded
                    let joursBureau = joursEGPGUM;
                    if (joursEGPGUM.length === 0) {
                        const { data } = await axiosInstance.get(
                            `/api/production/etat-global-production-gum/${period.exercice}-01/${selectedAgence?.codeAgence || selectedAgence}`
                        );
                        joursBureau = data;
                        setJoursEGPGUM(data);
                    }

                    // Export to Excel and PDF
                    const excelBureauSuccess = exportGUMProductionToExcel(
                        joursBureau,
                        `${period.exercice}-${selectedMonth || '01'}`,
                        selectedAgence?.nom || selectedAgence?.ville
                    );
                    const pdfBureauSuccess = exportGUMProductionToPDF(
                        joursBureau,
                        `${period.exercice}-${selectedMonth || '01'}`,
                        selectedAgence?.nom || selectedAgence?.ville
                    );

                    if (excelBureauSuccess && pdfBureauSuccess) {
                        message.success('Fichiers Excel et PDF téléchargés avec succès');
                    } else {
                        message.warning('Téléchargement partiel - vérifiez les fichiers');
                    }
                    break;

                default:
                    message.info('Fonctionnalité d\'export en cours de développement');
            }
        } catch (err) {
            console.error(err);
            message.error('Une erreur est survenue lors du téléchargement');
        } finally {
            setLoading(key, false);
        }
    };

    // Rendu de la section des filtres de période
    const renderPeriodFilters = () => (
        <Row gutter={[16, 16]}>
            {/* Exercice (Année) - Obligatoire */}
            <Col xs={24} sm={12} lg={6}>
                <div style={styles.filterLabel}>
                    <div style={{
                        ...styles.filterIcon,
                        background: 'rgba(36, 33, 102, 0.1)',
                        color: theme.colors.primary
                    }}>
                        <CalendarOutlined />
                    </div>
                    <div>
                        <Text strong style={{ color: theme.colors.textPrimary }}>Exercice</Text>
                        <Text type="danger"> *</Text>
                    </div>
                </div>
                <Select
                    placeholder="Sélectionner l'année"
                    style={{ width: '100%' }}
                    size="large"
                    value={selectedExercice}
                    onChange={(value) => {
                        setSelectedExercice(value);
                        // Reset les autres filtres de période
                        setSelectedMonth(null);
                        setSelectedRange(null);
                    }}
                    options={generateYears()}
                />
            </Col>

            {/* Type de période */}
            <Col xs={24} sm={12} lg={6}>
                <div style={styles.filterLabel}>
                    <div style={{
                        ...styles.filterIcon,
                        background: 'rgba(59, 130, 246, 0.1)',
                        color: theme.colors.info
                    }}>
                        <FilterOutlined />
                    </div>
                    <Text strong style={{ color: theme.colors.textPrimary }}>Type de période</Text>
                </div>
                <Select
                    style={{ width: '100%' }}
                    size="large"
                    value={periodType}
                    onChange={(value) => {
                        setPeriodType(value);
                        setSelectedMonth(null);
                        setSelectedRange(null);
                    }}
                    disabled={!selectedExercice}
                    options={[
                        { value: 'year', label: '📅 Année complète' },
                        { value: 'month', label: '📆 Mois spécifique' },
                        { value: 'range', label: '📊 Plage de dates' },
                    ]}
                />
            </Col>

            {/* Filtre conditionnel selon le type */}
            {periodType === 'month' && selectedExercice && (
                <Col xs={24} sm={12} lg={6}>
                    <div style={styles.filterLabel}>
                        <div style={{
                            ...styles.filterIcon,
                            background: 'rgba(16, 185, 129, 0.1)',
                            color: theme.colors.success
                        }}>
                            <CalendarOutlined />
                        </div>
                        <Text strong style={{ color: theme.colors.textPrimary }}>Mois</Text>
                    </div>
                    <Select
                        placeholder="Sélectionner le mois"
                        style={{ width: '100%' }}
                        size="large"
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        options={MONTHS}
                    />
                </Col>
            )}

            {periodType === 'range' && selectedExercice && (
                <Col xs={24} sm={12} lg={6}>
                    <div style={styles.filterLabel}>
                        <div style={{
                            ...styles.filterIcon,
                            background: 'rgba(245, 158, 11, 0.1)',
                            color: theme.colors.warning
                        }}>
                            <CalendarOutlined />
                        </div>
                        <Text strong style={{ color: theme.colors.textPrimary }}>Plage de dates</Text>
                    </div>
                    <RangePicker
                        style={{ width: '100%', height: '40px' }}
                        size="large"
                        format="DD/MM/YYYY"
                        value={selectedRange}
                        onChange={setSelectedRange}
                        disabledDate={(current) => {
                            // Limiter aux dates de l'exercice sélectionné
                            const year = parseInt(selectedExercice);
                            return current && (current.year() !== year);
                        }}
                        placeholder={['Date début', 'Date fin']}
                    />
                </Col>
            )}

            {/* Affichage de la période effective */}
            {getEffectivePeriod && (
                <Col xs={24} sm={12} lg={6}>
                    <div style={styles.filterLabel}>
                        <div style={{
                            ...styles.filterIcon,
                            background: 'rgba(16, 185, 129, 0.1)',
                            color: theme.colors.success
                        }}>
                            <CheckCircleOutlined />
                        </div>
                        <Text strong style={{ color: theme.colors.textPrimary }}>Période sélectionnée</Text>
                    </div>
                    <div style={{
                        padding: '10px 16px',
                        background: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '10px',
                        border: `1px solid ${theme.colors.success}`,
                    }}>
                        <Text strong style={{ color: theme.colors.success }}>
                            {getEffectivePeriod.displayText}
                        </Text>
                    </div>
                </Col>
            )}
        </Row>
    );

    // Rendu des filtres spécifiques à la branche
    const renderBranchFilters = () => (
        <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
            {branche === 'TPVM' && (
                <>
                    <Col xs={24} sm={12} lg={6}>
                        <div style={styles.filterLabel}>
                            <div style={{
                                ...styles.filterIcon,
                                background: 'rgba(139, 92, 246, 0.1)',
                                color: '#8b5cf6'
                            }}>
                                <FileTextOutlined />
                            </div>
                            <div>
                                <Text strong style={{ color: theme.colors.textPrimary }}>Catégorie</Text>
                                <Text type="danger"> *</Text>
                            </div>
                        </div>
                        <Select
                            placeholder="Sélectionner la catégorie"
                            style={{ width: '100%' }}
                            size="large"
                            value={selectedCategory?.codeCategorie}
                            onChange={(_, r) => setSelectedCategory(r.data)}
                            options={categories.map((cat) => ({
                                value: cat.codeCategorie,
                                label: cat.nom,
                                data: cat
                            }))}
                        />
                    </Col>

                    {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (
                        <Col xs={24} sm={12} lg={6}>
                            <div style={styles.filterLabel}>
                                <div style={{
                                    ...styles.filterIcon,
                                    background: 'rgba(236, 72, 153, 0.1)',
                                    color: '#ec4899'
                                }}>
                                    <BuildOutlined />
                                </div>
                                <Text strong style={{ color: theme.colors.textPrimary }}>Compagnie</Text>
                            </div>
                            <Select
                                placeholder="Toutes les compagnies"
                                style={{ width: '100%' }}
                                size="large"
                                allowClear
                                value={selectedCompagnie?.code}
                                onChange={(_, r) => setSelectedCompagnie(r?.data || null)}
                                options={compagnies.map((com) => ({
                                    value: com.code,
                                    label: com.raisonSociale,
                                    data: com
                                }))}
                            />
                        </Col>
                    )}
                </>
            )}

            {branche === 'GUM' && (
                <Col xs={24} sm={12} lg={6}>
                    <div style={styles.filterLabel}>
                        <div style={{
                            ...styles.filterIcon,
                            background: 'rgba(245, 158, 11, 0.1)',
                            color: theme.colors.warning
                        }}>
                            <BankOutlined />
                        </div>
                        <Text strong style={{ color: theme.colors.textPrimary }}>Bureau DTRF</Text>
                    </div>
                    <Select
                        placeholder="Sélectionner le bureau"
                        style={{ width: '100%' }}
                        size="large"
                        allowClear
                        value={selectedAgence?.codeAgence}
                        onChange={(_, r) => setSelectedAgence(r?.data || null)}
                        options={agences.map((ag) => ({
                            value: ag.codeAgence,
                            label: ag.nom,
                            data: ag
                        }))}
                    />
                </Col>
            )}
        </Row>
    );

    // Rendu des cartes de rapports
    const renderReportCards = () => (
        <Row gutter={[24, 24]}>
            {typeFiles[branche].map((file) => (
                <Col xs={24} sm={24} md={12} lg={8} key={file.key}>
                    <Card
                        hoverable
                        style={styles.reportCard}
                        bodyStyle={{ padding: 0 }}
                        className="report-card-hover"
                    >
                        {/* Header avec gradient */}
                        <div style={{
                            ...styles.reportCardHeader,
                            background: file.gradient,
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '-20px',
                                right: '-20px',
                                fontSize: '100px',
                                opacity: '0.1',
                            }}>
                                📊
                            </div>
                            <div style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                width: '56px',
                                height: '56px',
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                                color: '#ffffff',
                            }}>
                                {file.icon}
                            </div>
                            <h3 style={{
                                color: '#ffffff',
                                fontSize: '18px',
                                fontWeight: 700,
                                margin: 0,
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}>
                                {file.title}
                            </h3>
                        </div>

                        {/* Body */}
                        <div style={styles.reportCardBody}>
                            <p style={{
                                color: theme.colors.textSecondary,
                                fontSize: '14px',
                                lineHeight: '1.6',
                                marginBottom: '20px',
                                minHeight: '48px',
                            }}>
                                {file.description}
                            </p>

                            {/* Indicateurs de pré-requis */}
                            <div style={{ marginBottom: '20px' }}>
                                <Space wrap size={[8, 8]}>
                                    <Tag color={selectedExercice ? 'success' : 'default'}>
                                        {selectedExercice ? `✓ ${selectedExercice}` : '○ Exercice'}
                                    </Tag>
                                    {branche === 'TPVM' && (
                                        <Tag color={selectedCategory ? 'success' : 'default'}>
                                            {selectedCategory ? `✓ ${selectedCategory.nom || selectedCategory}` : '○ Catégorie'}
                                        </Tag>
                                    )}
                                    {file.key === 'etat_production_bureau' && (
                                        <Tag color={selectedAgence ? 'success' : 'default'}>
                                            {selectedAgence ? `✓ ${selectedAgence.nom || selectedAgence}` : '○ Bureau'}
                                        </Tag>
                                    )}
                                </Space>
                            </div>

                            {/* Boutons d'action */}
                            <Space style={{ width: '100%' }} direction="vertical" size={12}>
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    icon={<EyeOutlined />}
                                    loading={loadingStates[file.key]}
                                    onClick={() => handlePreview(file)}
                                    style={{
                                        ...styles.btn,
                                        ...styles.btnPrimary,
                                    }}
                                >
                                    {loadingStates[file.key] ? 'Chargement...' : 'Visualiser'}
                                </Button>
                                <Button
                                    size="large"
                                    block
                                    icon={<DownloadOutlined />}
                                    loading={loadingStates[`dl_${file.key}`]}
                                    onClick={() => handleDownload(file)}
                                    style={{
                                        ...styles.btn,
                                        ...styles.btnOutline,
                                    }}
                                >
                                    {loadingStates[`dl_${file.key}`] ? 'Préparation...' : 'Télécharger'}
                                </Button>
                            </Space>
                        </div>
                    </Card>
                </Col>
            ))}
        </Row>
    );

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={styles.pageBackground}>

                                {/* ═══ EN-TÊTE ═══ */}
                                <div style={styles.header}>
                                    <div style={styles.headerPattern} />
                                    <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                                        <Row justify="space-between" align="middle">
                                            <Col xs={24} md={14}>
                                                <Title level={2} style={{
                                                    color: '#ffffff',
                                                    margin: '0 0 8px 0',
                                                    fontWeight: 700,
                                                }}>
                                                    <BarChartOutlined style={{ marginRight: '12px' }} />
                                                    États de Production
                                                </Title>
                                                <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px' }}>
                                                    Visualisation et analyse des données de production par branche
                                                </Text>
                                            </Col>
                                            <Col xs={24} md={10} style={{ textAlign: 'right', marginTop: '16px' }}>
                                                {getEffectivePeriod && (
                                                    <div style={styles.periodBadge}>
                                                        <span style={styles.periodLabel}>Période active</span>
                                                        <span style={styles.periodValue}>
                                                            <CalendarOutlined style={{ marginRight: '8px' }} />
                                                            {getEffectivePeriod.displayText}
                                                        </span>
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 40px' }}>

                                    {/* ═══ FILTRES ═══ */}
                                    <Card style={styles.filterCard} bodyStyle={styles.filterSection}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '20px',
                                            paddingBottom: '16px',
                                            borderBottom: `2px solid ${theme.colors.borderLight}`,
                                        }}>
                                            <FilterOutlined style={{
                                                fontSize: '20px',
                                                color: theme.colors.primary,
                                                marginRight: '12px'
                                            }} />
                                            <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
                                                Configuration de la période
                                            </Title>
                                            <Tooltip title="Sélectionnez d'abord l'exercice (année), puis affinez avec un mois ou une plage de dates si nécessaire">
                                                <InfoCircleOutlined style={{
                                                    marginLeft: '12px',
                                                    color: theme.colors.textMuted,
                                                    cursor: 'help',
                                                }} />
                                            </Tooltip>
                                        </div>

                                        {renderPeriodFilters()}

                                        {(branche === 'TPVM' || branche === 'GUM') && (
                                            <>
                                                <Divider style={{ margin: '20px 0' }} />
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '16px',
                                                }}>
                                                    <Text strong style={{ color: theme.colors.textSecondary }}>
                                                        Filtres spécifiques à la branche {branche}
                                                    </Text>
                                                </div>
                                                {renderBranchFilters()}
                                            </>
                                        )}
                                    </Card>

                                    {/* ═══ ONGLETS BRANCHES ═══ */}
                                    <Card style={styles.tabsCard} bodyStyle={{ padding: '24px' }}>
                                        <Tabs
                                            activeKey={branche}
                                            onChange={(key) => {
                                                setBranche(key);
                                                setSelectedCategory(null);
                                                setSelectedAgence(null);
                                            }}
                                            size="large"
                                            tabBarStyle={{ marginBottom: 0, fontWeight: 600 }}
                                        >
                                            <TabPane
                                                tab={
                                                    <span style={{ fontSize: '15px', padding: '0 8px' }}>
                                                        <CarOutlined style={{ marginRight: '8px' }} />
                                                        TPV & MOTO
                                                    </span>
                                                }
                                                key="TPVM"
                                            />
                                            <TabPane
                                                tab={
                                                    <span style={{ fontSize: '15px', padding: '0 8px' }}>
                                                        <FileTextOutlined style={{ marginRight: '8px' }} />
                                                        GUM
                                                    </span>
                                                }
                                                key="GUM"
                                            />
                                            <TabPane
                                                tab={
                                                    <span style={{ fontSize: '15px', padding: '0 8px' }}>
                                                        <FileProtectOutlined style={{ marginRight: '8px' }} />
                                                        VT
                                                    </span>
                                                }
                                                key="VT"
                                            />
                                        </Tabs>
                                    </Card>

                                    {/* ═══ CARTES DE RAPPORTS ═══ */}
                                    {renderReportCards()}

                                </div>
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* ═══ MODALES ═══ */}
            <SDIModal
                width={1900}
                open={showEmissionNetteCedee}
                onCancel={() => setShowEmissionNetteCedee(false)}
                title="Émissions nettes cédées"
                zIndex={10000}
            >
                <div ref={emissionsRef}>
                    {selectedCategory && selectedExercice && (
                        <EmissionsNettesCedeesParExerciceEtCategorie
                            emissions={emissions}
                            categorie={selectedCategory?.codeCategorie === "M" ? "MOTO" : selectedCategory?.codeCategorie}
                            exercice={selectedExercice}
                        />
                    )}
                </div>
            </SDIModal>

            <SDIModal
                width={1400}
                open={showEtatGlobalProductionNationalGUM}
                onCancel={() => setShowEtatGlobalProductionNationalGUM(false)}
                title="État global de production GUM - National"
                zIndex={10000}
            >
                <div ref={etatGlobalNationalRef}>
                    {selectedExercice && selectedMonth && (
                        <EtatGlobalProductionMensuelDTRFNational
                            jours={joursEGPNGUM}
                            mois={`${selectedExercice}-${selectedMonth || '01'}`}
                        />
                    )}
                </div>
            </SDIModal>

            <SDIModal
                width={1300}
                open={showEtatGlobalProductionGUM}
                onCancel={() => setShowEtatGlobalProductionGUM(false)}
                title={`État de production GUM - ${selectedAgence?.nom || ''}`}
                zIndex={10000}
            >
                <div ref={etatGlobalBureauRef}>
                    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                        {selectedExercice && (
                            <EtatGlobalProductionMensuelDTRFNational
                                jours={joursEGPGUM}
                                mois={`${selectedExercice}-${selectedMonth || '01'}`}
                                bureau={selectedAgence?.ville}
                            />
                        )}
                    </Space>
                </div>
            </SDIModal>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>

            {/* Styles CSS pour les effets hover */}
            <style>{`
                .report-card-hover:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12) !important;
                }
            `}</style>
        </div>
    );
};

// Import manquant pour BarChartOutlined
const BarChartOutlined = () => (
    <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
        <path d="M888 792H200V168c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v688c0 4.4 3.6 8 8 8h752c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zM288 604a64 64 0 10128 0 64 64 0 10-128 0zm118-224a48 48 0 1096 0 48 48 0 10-96 0zm158 108a96 96 0 10192 0 96 96 0 10-192 0zm150-216a64 64 0 10128 0 64 64 0 10-128 0z" />
    </svg>
);

export default EtatsProductionGlobal;